import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, setDoc, doc, deleteDoc, query, where } from 'firebase/firestore';
import { AcademicCapIcon, PlusIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { Curriculum } from '../types';

export default function Curricula() {
  const { currentUser } = useAuth();
  const [curricula, setCurricula] = useState<Curriculum[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCurriculum, setNewCurriculum] = useState<Partial<Curriculum>>({
    title: '',
    description: '',
    department: '',
    targetAudience: '',
  });

  useEffect(() => {
    if (!currentUser) return;
    const fetchCurricula = async () => {
      const ref = collection(db, 'curricula');
      const q = query(ref, where('uid', '==', currentUser.uid));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => d.data() as Curriculum).sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
      setCurricula(data);
      setLoading(false);
    };
    fetchCurricula();
  }, [currentUser]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !newCurriculum.title?.trim()) return;
    const now = new Date().toISOString();
    const curriculum: Curriculum = {
      id: `cur-${Date.now()}`,
      title: newCurriculum.title.trim(),
      description: newCurriculum.description?.trim() || '',
      department: newCurriculum.department?.trim() || undefined,
      targetAudience: newCurriculum.targetAudience?.trim() || undefined,
      createdAt: now,
      updatedAt: now,
      uid: currentUser.uid,
    };
    await setDoc(doc(db, 'curricula', curriculum.id), curriculum);
    setCurricula(prev => [curriculum, ...prev]);
    setShowAddModal(false);
    setNewCurriculum({ title: '', description: '', department: '', targetAudience: '' });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu müfredatı ve altındaki tüm kursları silmek istediğinize emin misiniz?')) return;
    await deleteDoc(doc(db, 'curricula', id));
    setCurricula(prev => prev.filter(c => c.id !== id));
  };

  if (!currentUser) return <div className="p-8">Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Müfredatlar</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Çalışanlarınız için eğitim müfredatları oluşturun ve yönetin
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Yeni Müfredat
        </button>
      </div>

      {loading ? (
        <div className="card animate-pulse h-32" />
      ) : curricula.length === 0 ? (
        <div className="card text-center py-16">
          <AcademicCapIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Henüz müfredat yok
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
            İlk müfredatınızı oluşturarak başlayın. Müfredatlar altında kursları ekleyebilir,
            atamalar yapabilir ve ROI takibi yapabilirsiniz.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            İlk Müfredatı Oluştur
          </button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {curricula.map((cur) => (
            <div
              key={cur.id}
              className="card hover:shadow-lg transition-shadow group"
            >
              <Link
                to={`/app/curricula/${cur.id}`}
                className="block"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {cur.title}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {cur.description || 'Açıklama yok'}
                    </p>
                    {(cur.department || cur.targetAudience) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {cur.department && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                            {cur.department}
                          </span>
                        )}
                        {cur.targetAudience && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                            {cur.targetAudience}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-gray-400 group-hover:text-primary-600 flex-shrink-0 ml-2" />
                </div>
              </Link>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleDelete(cur.id);
                  }}
                  className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Sil
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Yeni Müfredat
              </h2>
              <form onSubmit={handleAdd} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Başlık *
                  </label>
                  <input
                    type="text"
                    value={newCurriculum.title || ''}
                    onChange={(e) => setNewCurriculum({ ...newCurriculum, title: e.target.value })}
                    className="input"
                    placeholder="Örn: Yeni Çalışan Oryantasyonu"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Açıklama
                  </label>
                  <textarea
                    value={newCurriculum.description || ''}
                    onChange={(e) => setNewCurriculum({ ...newCurriculum, description: e.target.value })}
                    className="input min-h-[80px]"
                    placeholder="Müfredat hakkında kısa açıklama"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Departman
                  </label>
                  <input
                    type="text"
                    value={newCurriculum.department || ''}
                    onChange={(e) => setNewCurriculum({ ...newCurriculum, department: e.target.value })}
                    className="input"
                    placeholder="Örn: İnsan Kaynakları"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Hedef Kitle
                  </label>
                  <input
                    type="text"
                    value={newCurriculum.targetAudience || ''}
                    onChange={(e) => setNewCurriculum({ ...newCurriculum, targetAudience: e.target.value })}
                    className="input"
                    placeholder="Örn: Yeni işe alınanlar"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="btn-secondary flex-1"
                  >
                    İptal
                  </button>
                  <button type="submit" className="btn-primary flex-1">
                    Oluştur
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
